import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Camera, ExternalLink } from "lucide-react";

/**
 * Conference photo gallery panel — visible in the Portal only for in-person
 * attendees (ticket_source==="face_to_face") and admins. Backend gates access
 * on `GET /api/photo-gallery` — this component silently hides itself if the
 * caller isn't entitled OR no URL has been configured yet.
 */
export default function PhotoGalleryPanel() {
  const [url, setUrl] = useState(null); // null = loading/hidden, string = show

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/photo-gallery");
        if (!alive) return;
        setUrl(r.data?.url || null);
      } catch {
        if (alive) setUrl(null);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!url) return null;

  return (
    <section
      id="photos"
      className="rounded-3xl overflow-hidden ring-1 ring-black/5 shadow-[0_18px_50px_-30px_rgba(15,23,42,0.55)]"
      data-testid="photo-gallery-panel"
    >
      <div className="relative bg-gradient-to-br from-[#0F172A] via-[#132444] to-[#28BBE3] px-6 sm:px-10 py-10 sm:py-14">
        {/* Soft grain */}
        <div
          className="absolute inset-0 opacity-[0.07] mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.7) 0%, transparent 40%), radial-gradient(circle at 70% 60%, rgba(255,255,255,0.4) 0%, transparent 45%)",
          }}
        />
        <div className="relative flex items-start justify-between gap-6 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            <div className="h-12 w-12 rounded-2xl bg-white/10 ring-1 ring-white/20 flex items-center justify-center shrink-0 backdrop-blur-sm">
              <Camera size={20} strokeWidth={1.6} className="text-white" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 font-semibold">Attended in Glasgow?</div>
              <h3 className="mt-1.5 font-heading text-2xl sm:text-3xl text-white font-medium tracking-tight leading-tight">
                Conference photos are ready.
              </h3>
              <p className="mt-2 text-sm text-white/70 max-w-lg leading-relaxed">
                A curated gallery from three days at the SEC — coffee-queue candids, keynote reactions, corridor chats. Yours to browse and download.
              </p>
            </div>
          </div>

          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-white text-slate-900 font-semibold text-sm hover:bg-slate-100 transition-colors shadow-[0_10px_30px_-12px_rgba(0,0,0,0.5)]"
            data-testid="photo-gallery-open-btn"
          >
            <ExternalLink size={15} strokeWidth={2.2} />
            Open photo gallery
          </a>
        </div>
      </div>
    </section>
  );
}
